/*
 * Typo and language tolerance for Laserlab search.
 * Shopify still retrieves and ranks results. This file only suggests a better query:
 * normalised spelling, English and everyday words mapped to the catalogue's own words,
 * and Damerau-Levenshtein matching against words staff use in product, treatment and category names.
 * The vocabulary is rendered by sections/laserlab-search-vocabulary.liquid, so new products are included automatically.
 */
(() => {
  if (window.LaserlabSearchTerms) return;

  const script = document.currentScript;
  const vocabularyURL = script?.dataset.vocabularyUrl || '/collections/all?section_id=laserlab-search-vocabulary';
  const cacheKey = `llSearchVocabulary:1:${window.Shopify?.theme?.id || ''}`;
  const cacheAge = 60 * 60 * 1000;

  /*
   * What visitors type -> the word the catalogue uses. Directional: the first item is the catalogue word.
   * Only general language and treatment areas. Never prescription medicine names.
   */
  const synonyms = [
    ['brasiliansk', 'brazilian', 'brazillian', 'brasilian', 'brasillian'],
    ['bikinilinje', 'bikini line', 'bikiniline', 'bikini area', 'bikinilinjen'],
    ['armhuler', 'armpit', 'armpits', 'underarm', 'underarms', 'under arm', 'under armene', 'armhule', 'armhulen', 'armhulene'],
    ['legger', 'legs', 'lower legs', 'lower leg', 'leggene'],
    ['lår', 'thigh', 'thighs', 'upper legs', 'upper leg', 'lårene'],
    ['hele ben', 'full legs', 'full leg', 'whole legs', 'whole leg', 'hele bena', 'hele beina', 'hele benet', 'hele beinet'],
    ['arm', 'arms', 'armer', 'armene', 'forearm', 'forearms'],
    ['ansikt', 'face', 'facial hair', 'ansiktet', 'ansiktshår'],
    ['bryst', 'chest'],
    ['rygg', 'back', 'upper back', 'lower back', 'ryggen'],
    ['hårfjerning', 'hair removal', 'hairremoval', 'laser hair removal', 'epilering', 'epilation', 'fjerne hår', 'fjerning av hår', 'hårreduksjon'],
    ['pigment', 'pigmentation', 'hyperpigmentation', 'hyperpigmentering', 'pigmentflekk', 'pigmentflekker', 'dark spots', 'sun spots', 'sunspots', 'age spots', 'brune flekker', 'solflekker', 'discoloration'],
    ['akne', 'acne', 'kviser', 'kvise', 'uren hud', 'urenheter', 'pimples', 'breakouts', 'blemishes'],
    ['arr', 'scars', 'scarring', 'acne scars', 'aknearr'],
    ['rynker', 'wrinkles', 'wrinkle', 'fine lines', 'fine linjer', 'anti wrinkle', 'antiwrinkle'],
    ['anti age', 'anti aging', 'antiaging', 'anti ageing', 'antiage'],
    ['fuktighetskrem', 'moisturizer', 'moisturising cream', 'moisturizing cream', 'fuktkrem'],
    ['rens', 'cleanser', 'cleansing', 'face wash', 'facewash', 'ansiktsrens'],
    ['solkrem', 'sunscreen', 'sun screen', 'sun cream', 'suncream', 'solfaktor'],
    ['oily', 'fet hud'],
    ['dry', 'tørr hud'],
    ['eye', 'eyes', 'eye cream', 'eye serum', 'øyne', 'øynene', 'øyekrem', 'øyeserum', 'øyeområdet', 'under øynene'],
    ['lip', 'lips', 'leppe', 'lepper', 'leppepomade', 'leppebalsam'],
    ['toner', 'ansiktsvann'],
    ['hyaluron', 'hyaluronic', 'hyaluronic acid'],
    ['electrolytes', 'elektrolytter', 'elektrolytt'],
    ['gavekort', 'gift card', 'giftcard', 'gift cards', 'gift voucher', 'voucher', 'presentkort', 'gavesertifikat'],
    ['konsultasjon', 'consultation', 'consultations', 'consult'],
    ['hudpleie', 'skincare', 'skin care', 'hudpleieprodukter', 'hudprodukter'],
    ['tilskudd', 'supplement', 'supplements', 'kosttilskudd']
  ];
  const stopwords = new Set(['og', 'eller', 'for', 'til', 'med', 'på', 'pa', 'en', 'et', 'ei', 'av', 'som', 'the', 'and', 'with', 'from', 'hva', 'hvor', 'noe', 'mot', 'uten', 'etter', 'min', 'mitt', 'mine', 'hele', 'alle', 'amp', 'nbsp']);
  // Everyday words one letter away from a catalogue word ("lege" / "legger", "mage" / "age"). They mean something else, so they are never corrected.
  const everyday = new Set(['lege', 'legen', 'mage', 'magen', 'menn', 'mann', 'herre', 'herrer', 'kvinne', 'kvinnen', 'kvinner', 'toll', 'lager']);

  function normalize(value) {
    return String(value || '').toLocaleLowerCase('nb-NO')
      .replace(/æ/g, 'ae').replace(/ø/g, 'o').replace(/å/g, 'a')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\p{L}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ').trim();
  }

  // Spelling key: z/s, c/k, ph/f and doubled letters are treated as the same sound.
  function key(value) {
    return normalize(value)
      .replace(/ph/g, 'f').replace(/th/g, 't').replace(/ck/g, 'k')
      .replace(/c(?=[eiy])/g, 's').replace(/c/g, 'k').replace(/q/g, 'k')
      .replace(/z/g, 's').replace(/w/g, 'v').replace(/x/g, 'ks')
      .replace(/(\p{L})\1+/gu, '$1');
  }

  // Damerau-Levenshtein (optimal string alignment), stopping early above the limit.
  function distance(a, b, limit = Infinity) {
    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > limit) return limit + 1;
    let before = null;
    let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
    for (let i = 1; i <= a.length; i += 1) {
      const current = [i];
      let rowMinimum = i;
      for (let j = 1; j <= b.length; j += 1) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        let value = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + cost);
        if (before && i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) value = Math.min(value, before[j - 2] + 1);
        current[j] = value;
        rowMinimum = Math.min(rowMinimum, value);
      }
      if (rowMinimum > limit) return limit + 1;
      before = previous;
      previous = current;
    }
    return previous[b.length];
  }

  // 0 edits below four letters, 1 edit for 4-7 letters, 2 edits from eight letters.
  function allowedEdits(word) {
    const length = normalize(word).replace(/\s/g, '').length;
    if (length >= 8) return 2;
    if (length >= 4) return 1;
    return 0;
  }

  // Norwegian and English endings, so "rynker" also finds "rynkebehandling".
  function stems(word) {
    const value = normalize(word);
    const result = [];
    const add = (stem) => { if (stem.length >= 4 && stem !== value) result.push(stem); };
    if (value.length >= 7 && value.endsWith('ene')) add(value.slice(0, -3));
    if (value.length >= 6 && value.endsWith('er')) add(value.slice(0, -1));
    if (value.length >= 6 && (value.endsWith('en') || value.endsWith('et'))) add(value.slice(0, -2));
    if (value.length >= 5 && value.endsWith('s') && !value.endsWith('ss')) add(value.slice(0, -1));
    return result;
  }

  // The bare stem or a compound ("rynke", "rynkebehandling"), not other inflections ("legges").
  function stemMatches(word, stem) {
    return word.startsWith(stem) && (word.length === stem.length || word.length >= stem.length + 3);
  }

  function tokens(value) {
    return String(value || '').toLocaleLowerCase('nb-NO').split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  }

  function createIndex(terms = [], known = []) {
    const single = new Map();
    const phrases = new Map();
    const entries = [];
    const keyed = new Map();
    const catalogue = new Set();
    const knownWords = new Set();
    const addKnown = (word) => { const value = normalize(word); if (value) knownWords.add(value); };
    const addTerm = (word) => {
      const value = normalize(word);
      if (value.length < 3 || !/\p{L}/u.test(value) || stopwords.has(value) || value.includes(' ')) return;
      addKnown(value);
      catalogue.add(value);
      const wordKey = key(value);
      if (keyed.has(wordKey)) return;
      const entry = { key: wordKey, target: String(word).toLocaleLowerCase('nb-NO'), term: true };
      keyed.set(wordKey, entry);
      entries.push(entry);
    };

    terms.forEach((term) => tokens(term).forEach(addTerm));
    known.forEach((word) => { const value = normalize(word); if (value.length >= 4 && !stopwords.has(value)) knownWords.add(value); });
    synonyms.forEach(([target, ...variants]) => {
      tokens(target).forEach(addKnown);
      variants.forEach((variant) => {
        const value = normalize(variant);
        const entry = { key: key(value), target, term: false, value };
        if (value.includes(' ')) phrases.set(value, entry);
        else {
          single.set(value, entry);
          entries.push(entry);
          if (!keyed.has(entry.key)) keyed.set(entry.key, entry);
        }
      });
    });
    const knownList = Array.from(knownWords);

    return {
      isCatalogue(word) {
        return catalogue.has(normalize(word));
      },
      // A catalogue word written as two words ("bikini linje" -> "bikinilinje").
      joined(words) {
        const entry = keyed.get(key(words.join('')));
        return entry?.term ? entry.target : null;
      },
      isKnown(word) {
        const value = normalize(word);
        if (value.length <= 2 || /\d/.test(value) || stopwords.has(value) || everyday.has(value) || knownWords.has(value)) return true;
        if (knownList.some((known) => known.startsWith(value))) return true;
        return stems(value).some((stem) => knownList.some((known) => stemMatches(known, stem)));
      },
      synonym(word) {
        return single.get(normalize(word))?.target || null;
      },
      phrase(words, { fuzzy = true } = {}) {
        const value = normalize(words);
        if (phrases.has(value)) return phrases.get(value).target;
        // Phrases allow one edit, two only for long phrases, so "lower lip" never becomes "lower leg".
        const letters = value.replace(/\s/g, '').length;
        const limit = letters >= 12 ? 2 : letters >= 4 ? 1 : 0;
        if (!limit || !fuzzy) return null;
        const phraseKey = key(value);
        let best = null;
        phrases.forEach((entry) => {
          if (entry.key[0] !== phraseKey[0]) return;
          const edits = distance(phraseKey, entry.key, limit);
          if (edits <= limit && (!best || edits < best.edits)) best = { edits, target: entry.target };
        });
        return best?.target || null;
      },
      lookup(word, { prefix = false } = {}) {
        const value = normalize(word);
        if (value.length < 3) return null;
        const wordKey = key(value);
        if (keyed.has(wordKey)) return keyed.get(wordKey).target;
        const limit = allowedEdits(value);
        if (!limit) return null;
        let best = null;
        // Fewest edits wins; then catalogue words over synonyms, then the shortest word.
        // A near match keeps the first letter and targets a word of four letters or more ("karriere" is not "barrier", "mage" is not "age").
        const consider = (entry, edits) => {
          if (edits > limit) return;
          if (edits > 0 && (entry.key.length < 4 || entry.key[0] !== wordKey[0])) return;
          const score = edits + (entry.term ? 0 : 0.1);
          if (!best || score < best.score || (score === best.score && entry.key.length < best.length)) best = { score, target: entry.target, length: entry.key.length };
        };
        entries.forEach((entry) => {
          consider(entry, distance(wordKey, entry.key, limit));
          // While typing, the start of a word may point to one catalogue word ("brazi" -> "brasiliansk").
          if (prefix && value.length >= 4 && entry.key.length > wordKey.length + 1) {
            const start = entry.key.slice(0, wordKey.length);
            if (start === wordKey) consider(entry, 0.5);
            else if (value.length >= 5) consider(entry, distance(wordKey, start, limit) + 0.5);
          }
        });
        return best?.target || null;
      }
    };
  }

  /*
   * Returns { query, changed }. Unknown words are corrected; words staff already use are left alone,
   * except explicit synonyms (for example "acne" -> "akne"). Callers decide whether the suggestion wins.
   */
  function correct(query, index, { prefix = true } = {}) {
    const words = tokens(query);
    const normalized = words.map(normalize);
    const result = [];
    let changed = false;
    for (let position = 0; position < words.length;) {
      let replacement = null;
      let size = 1;
      for (const length of [3, 2]) {
        if (position + length > words.length) continue;
        const group = normalized.slice(position, position + length);
        const hasUnknown = !group.every((word) => index.isKnown(word));
        const phrase = index.phrase(group.join(' '), { fuzzy: hasUnknown });
        if (phrase) { replacement = phrase; size = length; break; }
        // Split compounds: "bikini linje" -> "bikinilinje", "novo jet" -> "novojet".
        const joined = index.joined(group) || (hasUnknown ? index.lookup(group.join('')) : null);
        if (joined) { replacement = joined; size = length; break; }
      }
      if (!replacement) {
        const word = normalized[position];
        const isLast = position === words.length - 1;
        // A catalogue word inside a longer name stays as typed ("zero cleanser"); alone it may map ("acne" -> "akne").
        const synonym = words.length > 1 && index.isCatalogue(word) ? null : index.synonym(word);
        replacement = synonym || (index.isKnown(word) ? null : index.lookup(word, { prefix: prefix && isLast }));
      }
      const original = words.slice(position, position + size).join(' ');
      if (replacement && normalize(replacement) !== normalize(original)) {
        result.push(replacement);
        changed = true;
      } else {
        result.push(original);
      }
      position += size;
    }
    return { query: result.join(' '), changed };
  }

  let vocabulary = null;
  function load() {
    if (vocabulary) return vocabulary;
    vocabulary = (async () => {
      try {
        const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
        if (cached && Date.now() - cached.time < cacheAge && Array.isArray(cached.terms)) return createIndex(cached.terms, cached.known || []);
      } catch (_) { /* Storage can be unavailable in private windows. */ }
      try {
        const response = await fetch(vocabularyURL, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Vocabulary unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const data = JSON.parse(doc.querySelector('[data-search-vocabulary]')?.textContent || '{}');
        const terms = Array.isArray(data.terms) ? data.terms : [];
        const known = Array.isArray(data.known) ? data.known : [];
        try { sessionStorage.setItem(cacheKey, JSON.stringify({ time: Date.now(), terms, known })); } catch (_) { /* Optional cache. */ }
        return createIndex(terms, known);
      } catch (_) {
        // Synonyms still work without the catalogue words; try the catalogue again next time.
        vocabulary = null;
        return createIndex();
      }
    })();
    return vocabulary;
  }

  // Never keep results waiting on a slow vocabulary: after `wait` ms use synonyms only; the vocabulary keeps loading for the next search.
  let basic = null;
  function ready(wait = 1500) {
    const fallback = new Promise((resolve) => { setTimeout(() => resolve(basic || (basic = createIndex())), wait); });
    return Promise.race([load(), fallback]);
  }

  window.LaserlabSearchTerms = { normalize, key, distance, allowedEdits, stems, stemMatches, tokens, createIndex, correct, load, ready };
  document.dispatchEvent(new CustomEvent('laserlab:search-terms'));
})();
